import ReactModal from 'react-modal';
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';

import './Modal.css';
import { TimesIcon } from '../Icons';
import { Config } from '../../helpers/config';

type ModalProps = {
  children?: ReactNode | null;
  onOpen?: () => void;
  onClose?: () => void;
  hasClose?: boolean;
};

export type ModalRef = {
  openModal: () => void;
  closeModal: () => void;
};

const modalStyle = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 9999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '95%',
    maxWidth: '456px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
} as const;

// Used for a11y to hide to screen readers everything when Modal is open
if (Config.env !== 'test') {
  ReactModal.setAppElement('#root');
}

export const Modal: React.ForwardRefExoticComponent<
  ModalProps & React.RefAttributes<ModalRef>
> = forwardRef(
  ({ children, onOpen, onClose, hasClose = true }, forwardedRef) => {
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const openModal = useCallback(() => {
      onOpen?.();
      setIsModalVisible(true);
    }, [onOpen]);

    const closeModal = useCallback(() => {
      onClose?.();
      setIsModalVisible(false);
    }, [onClose]);

    useImperativeHandle(forwardedRef, () => ({
      openModal,
      closeModal,
    }));

    return (
      <ReactModal
        style={modalStyle}
        isOpen={isModalVisible}
        closeTimeoutMS={500}
      >
        <div className="modal--body">
          {hasClose && (
            <button
              onClick={closeModal}
              aria-label="Close modal"
              className="clickable modal--close-icon"
            >
              <TimesIcon width={20} height={20} />
            </button>
          )}
          {hasClose && <div className="modal--content">{children}</div>}
          {!hasClose && children}
        </div>
      </ReactModal>
    );
  }
);
